/* eslint-disable max-lines */
import {
    Box,
    Button,
    HStack,
    IconButton,
    Input,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Text,
    VStack,
} from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';
import {
    DayPicker,
    DayPickerSingleProps,
    CaptionProps,
    useNavigation,
} from 'react-day-picker';
import {
    format,
    isSameMonth,
    parse,
    add,
    sub,
    set,
    isAfter,
} from 'date-fns';
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { DateInput } from './date-input';
import { FaIcon } from '../fa-icon';

type BaseDayPickerProps = Omit<DayPickerSingleProps, 'selected' | 'onSelect' | 'mode'>;

export interface DatePickerProps extends BaseDayPickerProps {
    /**
   * Size of the input element
   *
   * Defaults to "md"
   */
    size?: React.ComponentProps<typeof DateInput>['size'];

    /**
   * Placeholder text to show if there is no dates selected
   *
   * Defaults to ""
   */
    placeholder?: React.ComponentProps<typeof DateInput>['placeholder'];

    /**
   * What to display when no date is selected
   *
   * Defaults to show the placeholder instead
   */
    noDateSelectedText?: string;

    /**
   * Controlled Date
   */
    value?: Date | null;

    /**
   * Callback fired after the date range has changed, will return ISO Dates
   */
    onChange?: (newDate: Date | null) => void;

    /**
   * Callback fired after the Date Range Picker popover has closed
   */
    onBlur?: () => void;

    /**
   * Show the input in an error state
   */
    isInvalid?: boolean;

    'aria-label'?: string;

    /**
   * The display format of the date
   * Defaults to dd/MM/yyyy
   */
    dateDisplayFormat?: string;

    /**
   * Allow the date to be cleared after having been selected
   *
   * Defaults to true
   */
    allowClear?: boolean;

    disabled?: boolean;

    /**
   * Whether the popover should close when a date is selected
   *
   * Defaults to true
   */
    closeWhenSelectDate?: boolean;

    /**
   * Show a button within the popover that will set the date to null (no date)
   *
   * Defaults to false
   */
    showNoDateButton?: boolean;

    /**
   * Change the text of the no date button
   *
   * Defaults to "No date"
   */
    noDateButtonText?: string;

    /**
     * Props to pass to the input element
     */
    inputProps?: React.ComponentProps<typeof Input>;

    showMonthPicker?: boolean;
}

const NAV_BUTTON_PROPS = {
    p: 0,
    borderRadius: 'full',
    colorScheme: 'gray',
    h: 7,
    w: 7,
    minW: 'unset',
    variant: 'ghost',
};

function DatePickerCaption(
    {
        toDate,
        displayMonth,
        showMonthPicker,
    }:
    CaptionProps & {
        toDate?: Date;
        showMonthPicker?: boolean;
    }
) {
    const {
        goToMonth, nextMonth, previousMonth, currentMonth, displayMonths,
    } = useNavigation();

    const displayPreviousMonth = useMemo(
        () => isSameMonth(displayMonth, displayMonths[0]),
        [displayMonths, currentMonth]
    );

    const displayNextMonth = useMemo(
        () => isSameMonth(displayMonth, displayMonths[displayMonths.length - 1]),
        [displayMonths, currentMonth]
    );

    return (
        <HStack pt={1} pb={1} gap={0} justifyContent="space-between">
            {displayPreviousMonth && (
                <IconButton
                    {...NAV_BUTTON_PROPS}
                    hidden={!displayPreviousMonth}
                    aria-label="Previous month"
                    icon={<FaIcon icon={faAngleLeft} />}
                    isDisabled={previousMonth === undefined}
                    onClick={() => {
                        if (previousMonth == null) {
                            return;
                        }

                        if (showMonthPicker === true) {
                            goToMonth(sub(currentMonth, { years: 1 }));
                        } else {
                            goToMonth(previousMonth);
                        }
                    }}
                />
            )}

            {showMonthPicker === false && (
                <Menu>
                    <MenuButton as={Button} variant="ghost" colorScheme="gray" fontWeight="bold">
                        {format(displayMonth, 'MMM')}
                    </MenuButton>
                    <MenuList>
                        {[...Array(12)].map((_, i) => (
                            <MenuItem
                                // eslint-disable-next-line react/no-array-index-key
                                key={`month_select_${i}`}
                                onClick={() => {
                                    goToMonth(set(currentMonth, { month: i }));
                                }}
                            >
                                <Text>
                                    {format(parse((i + 1).toString(), 'MM', new Date()), 'MMMM')}
                                </Text>
                            </MenuItem>
                        ))}
                    </MenuList>
                </Menu>
            )}

            <Menu>
                <MenuButton as={Button} variant="ghost" colorScheme="gray" fontWeight="bold">
                    {format(displayMonth, 'yyyy')}
                </MenuButton>
                <MenuList maxH="30vh" overflow="scroll">
                    {[...Array(Number(format(toDate ?? add(new Date(), { years: 5 }), 'yyyy')) - 1899)].map((_, i) => {
                        let currentYear = Number(format(toDate ?? add(new Date(), { years: 5 }), 'yyyy'));

                        return (
                            <MenuItem
                                // eslint-disable-next-line react/no-array-index-key
                                key={`year_select_${currentYear - i}`}
                                onClick={() => {
                                    goToMonth(
                                        set(currentMonth, { year: currentYear - i })
                                    );
                                }}
                            >
                                <Text>
                                    {format(
                                        parse((currentYear - i).toString(), 'yyyy', new Date()),
                                        'yyyy'
                                    )}
                                </Text>
                            </MenuItem>
                        );
                    })}
                </MenuList>
            </Menu>

            {displayNextMonth && (
                <IconButton
                    {...NAV_BUTTON_PROPS}
                    aria-label="Next month"
                    icon={<FaIcon icon={faAngleRight} />}
                    isDisabled={nextMonth === undefined}
                    onClick={() => {
                        if (nextMonth == null) {
                            return;
                        }

                        if (showMonthPicker === true) {
                            goToMonth(add(currentMonth, { years: 1 }));
                        } else {
                            goToMonth(nextMonth);
                        }
                    }}
                />
            )}
        </HStack>
    );
}

function MonthPicker(
    {
        onDateSelect,
        toDate,
    }:
    {
        onDateSelect: (selectedDate: Date) => void;
        toDate?: Date;
    }
) {
    const {
        currentMonth,
    } = useNavigation();

    return (
        <Box>
            {[...Array(12)].map((_, i) => {
                let monthValue = set(currentMonth, { month: i, date: 1 });

                return (
                    <Button
                    // eslint-disable-next-line react/no-array-index-key
                        key={`month_picker_item_${i}`}
                        variant="ghost"
                        colorScheme="gray"
                        fontWeight="normal"
                        isDisabled={toDate != null && isAfter(monthValue, toDate)}
                        onClick={() => {
                            onDateSelect(monthValue);
                        }}
                    >
                        <Text>
                            {format(parse((i + 1).toString(), 'MM', new Date()), 'MMMM')}
                        </Text>
                    </Button>
                );
            })}
        </Box>
    );
}

/**
 * Controlled Date Picker
 */
export function DatePicker({
    size = 'md',
    placeholder,
    noDateSelectedText = '',
    value,
    onChange = () => {},
    onBlur = () => {},
    isInvalid = false,
    allowClear = true,
    disabled = false,
    dateDisplayFormat = 'dd/MM/yyyy',
    closeWhenSelectDate = true,
    showNoDateButton = false,
    noDateButtonText = 'No date',
    'aria-label': ariaLabel,
    inputProps = {},
    showMonthPicker = false,
    ...dayPickerProps
}: DatePickerProps) {
    // Default the selected month to be the value we've been provided if we have one
    const [selectedMonth, setSelectedMonth] = useState<Date | undefined>(
        () => value ?? undefined
    );

    const onClear = () => onChange(null);

    const onDateSelect = (selectedDate: Date | undefined) => {
        // If date picker has cleared the selected dates,
        // we will do the same
        if (selectedDate === undefined) {
            onClear();
            return;
        }

        onChange(selectedDate);
        setSelectedMonth(selectedDate);
    };

    return (
        <Popover
            placement="bottom-start"
            returnFocusOnClose={false}
            onClose={onBlur}
            isLazy
        >
            {({ isOpen, onClose }) => (
                <>
                    <PopoverTrigger>
                        <DateInput
                            {...inputProps}
                            size={size}
                            placeholder={placeholder}
                            isPopoverOpen={isOpen}
                            selectedDate={value}
                            isInvalid={isInvalid}
                            aria-label={ariaLabel}
                            noDateSelectedText={noDateSelectedText}
                            allowClear={allowClear}
                            disabled={disabled}
                            onClear={() => {
                                onClear();
                                onClose();
                            }}
                            displayFormat={dateDisplayFormat}
                            required
                        />
                    </PopoverTrigger>
                    <PopoverContent w="max-content">
                        <PopoverArrow />
                        <PopoverBody>
                            <VStack alignItems="stretch">
                                <DayPicker
                                    {...dayPickerProps}
                                    mode="single"
                                    selected={value ?? undefined}
                                    onSelect={(day) => {
                                        onDateSelect(day);

                                        if (closeWhenSelectDate) {
                                            onClose();
                                        }
                                    }}
                                    month={selectedMonth}
                                    onMonthChange={setSelectedMonth}
                                    components={{
                                        Caption:
                                        (props) => DatePickerCaption({
                                            ...props,
                                            showMonthPicker,
                                            toDate: dayPickerProps.toDate,
                                        }),
                                        DayContent: showMonthPicker ? () => null : undefined,
                                        Day: showMonthPicker ? () => null : undefined,
                                        // HeadRow: showMonthPicker ? () => null : undefined,
                                        Row: showMonthPicker ? () => null : undefined,
                                        HeadRow: showMonthPicker
                                            ? () => MonthPicker({
                                                toDate: dayPickerProps.toDate,
                                                onDateSelect: (_date) => {
                                                    onDateSelect(_date);
                                                    onClose();
                                                },
                                            })
                                            : undefined,
                                    }}

                                />
                                {showNoDateButton && (
                                    <Button
                                        colorScheme="muted"
                                        size="sm"
                                        onClick={() => {
                                            onClear();

                                            if (closeWhenSelectDate) {
                                                onClose();
                                            }
                                        }}
                                    >
                                        {noDateButtonText}
                                    </Button>
                                )}
                            </VStack>
                        </PopoverBody>
                    </PopoverContent>
                </>
            )}
        </Popover>
    );
}
